import { useState, useEffect, useCallback } from "react";
import { fetchAddinConfig } from "../services/api";
import type { AddinConfigResponse } from "../types";

interface UseAddinConfigResult {
  config: AddinConfigResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch add-in configuration from backend.
 */
export function useAddinConfig(email: string | null): UseAddinConfigResult {
  const [config, setConfig] = useState<AddinConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!email) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchAddinConfig(email);
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load configuration");
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  };
}
